<template>
  <div>
    <div class="page-head">
      <div>
        <h2>首页与起始页配置</h2>
        <p>分别维护小程序首页内容、登录起始页图片与文字，以及分享卡片标题。</p>
      </div>
      <el-button v-if="total === 0" type="primary" @click="openCreate">创建主视觉</el-button>
    </div>

    <el-card shadow="never">
      <paged-table
        :rows="rows"
        :loading="loading"
        :page="page"
        :page-size="pageSize"
        :total="total"
        @page-change="page = $event; fetchList()"
        @size-change="pageSize = $event; page = 1; fetchList()"
      >
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column label="预览" width="180">
          <template slot-scope="{ row }">
            <el-image
              v-if="row.hero_url"
              class="banner-thumb"
              :src="row.hero_url"
              :preview-src-list="[row.hero_url]"
              fit="cover"
            />
            <span v-else class="muted">未上传</span>
          </template>
        </el-table-column>
        <el-table-column prop="title" label="第一行文案" min-width="180" />
        <el-table-column prop="subtitle" label="第二行文案" min-width="220" />
        <el-table-column label="起始页文字" min-width="240">
          <template slot-scope="{ row }"><strong>{{ row.start_title || '点击登录' }}</strong><div class="start-subtitle-preview">{{ row.start_subtitle || '未设置副标题' }}</div></template>
        </el-table-column>
        <el-table-column label="更新时间" width="180"><template slot-scope="{ row }">{{ row.updated_at | dateTime }}</template></el-table-column>
        <el-table-column label="操作" width="120">
          <template slot-scope="{ row }">
            <el-button type="text" @click="openEdit(row)">编辑</el-button>
          </template>
        </el-table-column>
      </paged-table>
    </el-card>

    <el-dialog
      :title="drawerMode === 'create' ? '创建首页与起始页配置' : '编辑首页与起始页配置'"
      :visible.sync="drawerVisible"
      width="560px"
      destroy-on-close
    >
      <el-form ref="form" class="drawer-form" :model="form" :rules="rules" label-width="120px">
        <el-divider content-position="left">小程序首页</el-divider>
        <el-form-item label="顶部大图" prop="hero_url">
          <asset-upload v-model="form.hero_url" type="image" category="home" />
        </el-form-item>
        <el-form-item label="第一行文案" prop="title">
          <el-input v-model="form.title" />
        </el-form-item>
        <el-form-item label="第二行文案">
          <el-input v-model="form.subtitle" />
        </el-form-item>
        <el-divider content-position="left">登录起始页图片与文字</el-divider>
        <el-alert type="info" :closable="false" title="这里的标题和副标题会显示在用户打开小程序后首先看到的起始登录页。" />
        <el-form-item label="登录起始页图" prop="start_hero_url">
          <asset-upload v-model="form.start_hero_url" type="image" category="login" />
        </el-form-item>
        <el-form-item label="起始页主标题" prop="start_title">
          <el-input v-model="form.start_title" maxlength="120" show-word-limit placeholder="例如：点击登录" />
        </el-form-item>
        <el-form-item label="起始页副标题" prop="start_subtitle">
          <el-input v-model="form.start_subtitle" type="textarea" :rows="3" maxlength="255" show-word-limit placeholder="登录后查看完整旅行内容" />
        </el-form-item>
        <el-divider content-position="left">分享设置</el-divider>
        <el-form-item label="分享卡片标题" prop="share_title">
          <el-input v-model="form.share_title" maxlength="120" show-word-limit placeholder="分享给好友时显示的标题" />
        </el-form-item>
      </el-form>
      <div class="drawer-footer">
        <el-button @click="drawerVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="save">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import AssetUpload from "../components/AssetUpload.vue";
import PagedTable from "../components/PagedTable.vue";
import { createResource, listResource, updateResource } from "../api/resources";

export default {
  name: "HomeContent",
  components: { AssetUpload, PagedTable },
  data() {
    return {
      loading: false,
      saving: false,
      drawerVisible: false,
      drawerMode: "create",
      rows: [],
      total: 0,
      page: 1,
      pageSize: 20,
      form: this.emptyForm(),
      rules: {
        hero_url: [{ required: true, message: "请上传顶部大图", trigger: "change" }],
        start_hero_url: [{ required: true, message: "请上传登录起始页图", trigger: "change" }],
        title: [{ required: true, message: "请填写第一行文案", trigger: "blur" }],
        start_title: [{ required: true, message: "请填写起始页主标题", trigger: "blur" }],
        start_subtitle: [{ required: true, message: "请填写起始页副标题", trigger: "blur" }],
        share_title: [{ required: true, message: "请填写分享卡片标题", trigger: "blur" }]
      }
    };
  },
  mounted() {
    this.fetchList();
  },
  methods: {
    emptyForm() {
      return {
        hero_url: "",
        start_hero_url: "",
        start_title: "点击登录",
        start_subtitle: "登录后查看完整旅行内容",
        share_title: "目的地定制游",
        title: "",
        subtitle: ""
      };
    },
    async fetchList() {
      this.loading = true;
      try {
        const res = await listResource("home-configs", { page: this.page, pageSize: this.pageSize });
        this.rows = res.data && res.data.items ? res.data.items : [];
        this.total = res.data && res.data.total ? res.data.total : 0;
      } finally {
        this.loading = false;
      }
    },
    openCreate() {
      this.drawerMode = "create";
      this.form = this.emptyForm();
      this.drawerVisible = true;
    },
    openEdit(row) {
      this.drawerMode = "edit";
      this.form = { ...this.emptyForm(), ...row };
      this.drawerVisible = true;
    },
    save() {
      this.$refs.form.validate(async (valid) => {
        if (!valid) return;
        this.saving = true;
        try {
          if (this.drawerMode === "create") await createResource("home-configs", this.form);
          else await updateResource("home-configs", this.form.id, this.form);
          this.$message.success("首页主视觉已保存");
          this.drawerVisible = false;
          this.fetchList();
        } finally {
          this.saving = false;
        }
      });
    }
  }
};
</script>

<style scoped>
.banner-thumb {
  width: 132px;
  height: 74px;
  overflow: hidden;
  border-radius: 6px;
  background: #f5f7fa;
  box-shadow: inset 0 0 0 1px rgba(21, 23, 28, 0.08);
}

.muted {
  color: #909399;
}
.start-subtitle-preview { margin-top: 6px; color: #909399; font-size: 12px; }
.drawer-form ::v-deep .el-alert { margin-bottom: 18px; }
</style>
